import { useEffect, useMemo, useState } from "react";

import { kuehneNagel2026 } from "../data/tariffs/kuehneNagel2026.ts";
import { calculateFreightPrice } from "../utils/calculateFreightPrice.ts";
import { getRelationLabel, getTariffs, normalizeTariff } from "../utils/tariffImportUtils";
import TariffInputForm from "./TariffInputForm";
import TariffResultCard from "./TariffResultCard";
import { SuccessToast, useSuccessFeedback } from "./useSuccessFeedback";

const savedOffersKey = "zentraqTariffOffers";

const baseInitialForm = {
  carrierId: "kuehne-nagel",
  tariffTypeId: "outbound-de",
  senderPostalCode: "94116",
  recipientPostalCode: "10115",
  palletCount: 3,
  packageTypeId: "euroPallet",
  lengthCm: 120,
  widthCm: 80,
  heightCm: 180,
  actualWeightKg: 1200,
  customer: "",
  note: "",
  surcharges: {
    manualDiesel: { enabled: false, percentage: "" },
    manualToll: { enabled: false, amount: "" },
    transportInsurance: { enabled: false, goodsValue: "" },
    capitalProvision: { enabled: false, percentage: 3 },
  },
};

function loadSavedOffers() {
  try {
    return JSON.parse(localStorage.getItem(savedOffersKey) || "[]");
  } catch (error) {
    return [];
  }
}

function saveOffers(offers) {
  localStorage.setItem(savedOffersKey, JSON.stringify(offers));
}

function buildPostalPrefixMap(postalCodeZones = []) {
  const map = {};

  postalCodeZones.forEach((entry) => {
    const from = Number(entry.fromPrefix);
    const to = Number(entry.toPrefix);

    if (Number.isNaN(from) || Number.isNaN(to)) {
      return;
    }

    for (let prefix = from; prefix <= to; prefix += 1) {
      map[String(prefix).padStart(2, "0")] = Number(entry.zone);
    }
  });

  return map;
}

function mapSurchargeDefinition(surcharge) {
  const calculationType = String(surcharge.calculationType || "").toLowerCase();
  const isSubtotalPercentage = calculationType.includes("netto") || calculationType.includes("rechnungsbetrag");
  const isPercentage = calculationType.includes("prozent");

  return {
    id: surcharge.id || surcharge.code || surcharge.label,
    code: surcharge.code,
    label: surcharge.code ? `${surcharge.code} - ${surcharge.label}` : surcharge.label,
    type: isPercentage
      ? isSubtotalPercentage
        ? "percentageOfSubtotal"
        : "percentageOfBaseFreight"
      : "fixedAmount",
    defaultPercentage: isPercentage ? Number(surcharge.value || 0) : undefined,
    defaultAmount: !isPercentage ? Number(surcharge.value || 0) : undefined,
    unit: isPercentage ? "%" : "EUR",
  };
}

function adaptImportedTariff(importedTariff) {
  if (!importedTariff) {
    return kuehneNagel2026;
  }

  const zonesByPostalPrefix = buildPostalPrefixMap(importedTariff.postalCodeZones);
  const insuranceDefinitions = [{
    id: "transportInsurance",
    label: "Transportversicherung",
    type: "insuranceTable",
    unit: "EUR",
    insuranceTable: importedTariff.insuranceTable || [],
  }];

  const surchargeDefinitions = [
    { id: "manualDiesel", label: "Dieselzuschlag manuell", type: "percentageOfBaseFreight", unit: "%" },
    { id: "manualToll", label: "Maut manuell", type: "fixedAmount", unit: "EUR" },
    ...insuranceDefinitions,
    ...(importedTariff.surcharges || []).map(mapSurchargeDefinition),
  ];

  return {
    id: importedTariff.id,
    carrierId: importedTariff.id,
    carrierName: importedTariff.carrierName,
    tariffYear: new Date(importedTariff.validFrom || Date.now()).getFullYear(),
    currency: importedTariff.currency || "EUR",
    vatRate: 0.19,
    volumetricWeightFactorKgPerCbm: Number(importedTariff.packageRules?.cbmKg || 150),
    sourceNote: `Aktiver Tarif: ${importedTariff.tariffName}. Gueltig ${importedTariff.validFrom || "-"} bis ${importedTariff.validTo || "-"}.`,
    tariffTypes: [{
      id: importedTariff.id,
      label: `${getRelationLabel(importedTariff)} ${importedTariff.tariffType}`,
      originCountry: importedTariff.country || "DE",
      destinationCountry: importedTariff.country || "DE",
      defaultSenderPostalCode: "94116",
      zonesByPostalPrefix,
      weightBreaksKg: importedTariff.weightBreaks || [],
      pricesByWeightAndZone: importedTariff.prices || {},
    }],
    packageMinimumWeightsKg: {
      euroPallet: { label: "Europalette / Einwegpalette", minimumWeightKg: Number(importedTariff.packageRules?.euroPalletKg || 175) },
      cageBox: { label: "Gitterbox", minimumWeightKg: Number(importedTariff.packageRules?.cagePalletKg || 250) },
      halfPallet: { label: "Halbpalette", minimumWeightKg: Number(importedTariff.packageRules?.halfPalletKg || 90) },
      quarterPallet: { label: "Viertelpalette", minimumWeightKg: Number(importedTariff.packageRules?.quarterPalletKg || 60) },
    },
    insuranceTable: importedTariff.insuranceTable || [],
    toll: importedTariff.toll || [],
    internationalToll: importedTariff.internationalToll || [],
    dieselTable: importedTariff.dieselTable || [],
    country: importedTariff.country,
    relation: getRelationLabel(importedTariff),
    surchargeDefinitions,
  };
}

const money = (value) =>
  Number(value || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

export default function TariffCalculatorPage({ suppliers, user }) {
  const activeTariffs = useMemo(
    () => getTariffs().map(normalizeTariff).filter((tariff) => tariff.status === "active" || tariff.isActive),
    []
  );
  const [selection, setSelection] = useState(() => ({
    carrierName: activeTariffs[0]?.carrierName || "",
    relation: activeTariffs[0] ? getRelationLabel(activeTariffs[0]) : "",
    tariffType: activeTariffs[0]?.tariffType || "",
  }));

  const carriers = useMemo(() => Array.from(new Set(activeTariffs.map((tariff) => tariff.carrierName))), [activeTariffs]);
  const relations = useMemo(
    () => Array.from(new Set(activeTariffs.filter((tariff) => tariff.carrierName === selection.carrierName).map(getRelationLabel))),
    [activeTariffs, selection.carrierName]
  );
  const tariffTypes = useMemo(
    () => Array.from(new Set(activeTariffs.filter((tariff) => tariff.carrierName === selection.carrierName && getRelationLabel(tariff) === selection.relation).map((tariff) => tariff.tariffType))),
    [activeTariffs, selection.carrierName, selection.relation]
  );
  const selectedImportedTariff = useMemo(() => {
    return activeTariffs.find((tariff) =>
      tariff.carrierName === selection.carrierName &&
      getRelationLabel(tariff) === selection.relation &&
      tariff.tariffType === selection.tariffType
    ) || null;
  }, [activeTariffs, selection]);
  const tariff = useMemo(() => adaptImportedTariff(selectedImportedTariff), [selectedImportedTariff]);
  const initialForm = useMemo(() => ({
    ...baseInitialForm,
    carrierId: tariff.carrierId || "kuehne-nagel",
    tariffTypeId: tariff.tariffTypes?.[0]?.id || "outbound-de",
  }), [tariff]);
  const [form, setForm] = useState(initialForm);
  const [result, setResult] = useState(() => calculateFreightPrice(initialForm, tariff));
  const [errors, setErrors] = useState([]);
  const [savedOffers, setSavedOffers] = useState(loadSavedOffers);
  const { successVisible, triggerSuccessFeedback } = useSuccessFeedback();

  const latestOffers = useMemo(() => [...savedOffers].reverse().slice(0, 8), [savedOffers]);

  useEffect(() => {
    const nextResult = calculateFreightPrice(form, tariff);
    setResult(nextResult);
    setErrors(nextResult.ok ? [] : nextResult.errors);
  }, [form, tariff]);

  const updateSelection = (patch) => {
    const nextSelection = { ...selection, ...patch };
    const nextRelations = Array.from(new Set(activeTariffs.filter((tariffItem) => tariffItem.carrierName === nextSelection.carrierName).map(getRelationLabel)));
    if (!nextRelations.includes(nextSelection.relation)) {
      nextSelection.relation = nextRelations[0] || "";
    }
    const nextTypes = Array.from(new Set(activeTariffs.filter((tariffItem) => tariffItem.carrierName === nextSelection.carrierName && getRelationLabel(tariffItem) === nextSelection.relation).map((tariffItem) => tariffItem.tariffType)));
    if (!nextTypes.includes(nextSelection.tariffType)) {
      nextSelection.tariffType = nextTypes[0] || "";
    }

    const nextTariff = activeTariffs.find((tariffItem) => tariffItem.carrierName === nextSelection.carrierName && getRelationLabel(tariffItem) === nextSelection.relation && tariffItem.tariffType === nextSelection.tariffType);
    const adapted = adaptImportedTariff(nextTariff);
    const nextForm = {
      ...form,
      carrierId: adapted.carrierId || "kuehne-nagel",
      tariffTypeId: adapted.tariffTypes?.[0]?.id || "outbound-de",
      surcharges: baseInitialForm.surcharges,
    };

    setSelection(nextSelection);
    setForm(nextForm);
    setResult(calculateFreightPrice(nextForm, adapted));
    setErrors([]);
  };

  const handleCalculate = () => {
    const nextResult = calculateFreightPrice(form, tariff);
    setResult(nextResult);
    setErrors(nextResult.ok ? [] : nextResult.errors);
  };

  const handleSaveOffer = () => {
    const nextResult = calculateFreightPrice(form, tariff);
    setResult(nextResult);
    setErrors(nextResult.ok ? [] : nextResult.errors);

    if (!nextResult.ok) {
      return;
    }

    const offer = {
      id: `tariff-offer-${Date.now()}`,
      date: new Date().toLocaleString("de-DE"),
      user: user?.username || "",
      customer: form.customer,
      senderPostalCode: form.senderPostalCode,
      recipientPostalCode: form.recipientPostalCode,
      weightKg: nextResult.breakdown.chargeableWeightKg,
      palletCount: nextResult.breakdown.palletCount,
      netPrice: nextResult.pricing.netTotal,
      note: form.note.trim(),
    };
    const nextOffers = [...savedOffers, offer];

    setSavedOffers(nextOffers);
    saveOffers(nextOffers);
    triggerSuccessFeedback();
  };

  return (
    <div className="page-stack tariff-page">
      <SuccessToast visible={successVisible} />

      <div>
        <p className="eyebrow">Mehraufwand</p>
        <h1 className="page-title">Tarifrechner</h1>
        <p className="section-text">
          Berechnung mit dem ausgewaehlten aktiven Tarif.
        </p>
      </div>

      <section className="card tariff-import-card">
        <div className="section-head">
          <div>
            <h2>Aktiven Tarif auswaehlen</h2>
            <p className="section-text">Es werden nur Tarife mit Status aktiv angeboten.</p>
          </div>
        </div>
        {activeTariffs.length === 0 ? (
          <div className="message message-info">Kein aktiver Importtarif vorhanden. Es wird die Demo-Tabelle verwendet.</div>
        ) : (
          <div className="form-grid columns-3 tariff-form-grid">
            <label className="field">
              <span>Spedition</span>
              <select value={selection.carrierName} onChange={(event) => updateSelection({ carrierName: event.target.value })}>
                {carriers.map((carrier) => <option key={carrier} value={carrier}>{carrier}</option>)}
              </select>
            </label>
            <label className="field">
              <span>Land/Relation</span>
              <select value={selection.relation} onChange={(event) => updateSelection({ relation: event.target.value })}>
                {relations.map((relation) => <option key={relation} value={relation}>{relation}</option>)}
              </select>
            </label>
            <label className="field">
              <span>Tarifart</span>
              <select value={selection.tariffType} onChange={(event) => updateSelection({ tariffType: event.target.value })}>
                {tariffTypes.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </label>
          </div>
        )}
      </section>

      <div className="tariff-layout">
        <TariffInputForm
          form={form}
          tariff={tariff}
          suppliers={suppliers}
          errors={errors}
          onChange={setForm}
          onCalculate={handleCalculate}
          onSaveOffer={handleSaveOffer}
        />
        <TariffResultCard result={result} />
      </div>

      <section className="card">
        <div className="section-head">
          <div>
            <h2>Gespeicherte Angebote</h2>
            <p className="section-text">Lokal gespeicherte Angebotsberechnungen.</p>
          </div>
        </div>

        {latestOffers.length === 0 ? (
          <div className="empty-state">Noch keine Angebote gespeichert.</div>
        ) : (
          <div className="table-wrap">
            <table className="data-table tariff-offers-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Benutzer</th>
                  <th>Kunde</th>
                  <th>PLZ</th>
                  <th>Gewicht</th>
                  <th>Paletten</th>
                  <th>Netto</th>
                  <th>Notiz</th>
                </tr>
              </thead>
              <tbody>
                {latestOffers.map((offer) => (
                  <tr key={offer.id}>
                    <td>{offer.date}</td>
                    <td>{offer.user || "-"}</td>
                    <td>{offer.customer || "-"}</td>
                    <td>{offer.senderPostalCode} -&gt; {offer.recipientPostalCode}</td>
                    <td>{offer.weightKg} kg</td>
                    <td>{offer.palletCount}</td>
                    <td>{money(offer.netPrice)}</td>
                    <td>{offer.note || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}






